import React, { useState, useEffect, useMemo } from 'react';
import {
  Col,
  Card,
  CardBody,
  Row,
  Button,
  TabPane,
  TabContent,
} from 'reactstrap';
import { Prompt } from 'react-router-dom';
import { groupBy, values } from 'ramda';
import CheckIcon from 'mdi-react/CheckIcon';
import TableIcon from 'mdi-react/TableIcon';
import AccountGroupIcon from 'mdi-react/AccountGroupIcon';
import NewspaperIcon from 'mdi-react/NewspaperIcon';
import StadiumIcon from 'mdi-react/StadiumIcon';
import SitemapIcon from 'mdi-react/SitemapIcon';
import CalendarIcon from 'mdi-react/CalendarIcon';
import TicketIcon from 'mdi-react/TicketIcon';
import DragIcon from 'mdi-react/DragIcon';
import WalletMembershipIcon from 'mdi-react/WalletMembershipIcon';
import GavelIcon from 'mdi-react/GavelIcon';
import YoutubeIcon from 'mdi-react/YoutubeIcon';
import ClubheartIcon from '../../shared/components/custom/ClubheartIcon';
import BellAlertIcon from 'mdi-react/BellAlertIcon';
import ImageAreaIcon from 'mdi-react/ImageAreaIcon';
import BasketIcon from 'mdi-react/BasketIcon';
import StarCircleIcon from 'mdi-react/StarCircleIcon';
import BrightnessPercentIcon from 'mdi-react/BrightnessPercentIcon';
import CommentOutlineIcon from 'mdi-react/CommentOutlineIcon';
import ImageOutlineIcon from 'mdi-react/ImageOutlineIcon';
import VideoIcon from 'mdi-react/VideoIcon';
import SoccerFieldIcon from 'mdi-react/SoccerFieldIcon';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import Loading from '../../shared/components/custom/Loading';
import EmptyStateInfo from '../../shared/components/custom/EmptyStateInfo';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import componentName from './componentName';
import useConfig from '../../util/useConfig';
import Config from '../../config';
import NavTab from '../../shared/components/custom/NavTab';
import MoreLinkModal from './MoreLinkModal';
import MoreIconModal from './MoreIconModal';
import DividerModal from './DividerModal';
import AppIcon, { hasAppIcon } from './AppIcon';
import MoreMenuEditor, { PALETTE, PALETTE_LINK } from './MoreMenuEditor';
import MorePhonePreview from './MorePhonePreview';
import BottomMenuEditor, { MAX_TABS } from './BottomMenuEditor';
import HomePhonePreview from './HomePhonePreview';

// A link the club adds to the More menu (saved with value 'link'), opened
// like a spotlight tile: { label, contentType, contentId }
const LINK_VALUE = 'link';
const isLinkItem = (item) => !!item?.link;
// The app's icon for a link of each type, when none is chosen (MoreContentScreen)
const LINK_DEFAULT_ICON = {
  NEWS: 'newspaper',
  WEBVIEW_LINK: 'globe',
  EXTERNAL_LINK: 'external-link',
};
// The app icon a More row shows here: its chosen one, or a link's default.
// null for the app's own rows without one (the app picks theirs).
const appIconOf = (item) => {
  if (item?.icon && hasAppIcon(item.icon)) {
    return item.icon;
  }
  return isLinkItem(item)
    ? LINK_DEFAULT_ICON[item.link.contentType] || 'link'
    : null;
};
// A chosen icon is saved with the row ({ icon }); none means the app's default
const withIcon = (row, item) => (item.icon ? { ...row, icon: item.icon } : row);
const newLinkId = () =>
  `link-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
// A divider in the More menu (saved with value 'divider'): the items after it,
// up to the next one, are a group in the app: { title, layout: list | grid }
const DIVIDER_VALUE = 'divider';
const isDividerItem = (item) => !!item?.divider;
const newDividerId = () =>
  `divider-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
// The member's own row (My memberships) is always first in the app's More
// menu, for fans with a membership; it isn't placed with the rest. Turned
// off, it's saved as { value: 'tierBanner', hidden: true }
const MEMBER_ROW_VALUE = 'tierBanner';

const getIconComponent = (type) => {
  switch (true) {
    case type.split('-')[0] === LINK_VALUE:
      return LinkVariantIcon;
    case type.split('-')[0] === 'spotlight':
      return ImageAreaIcon;
    case type === 'stats':
    case type === 'standings':
    case type === 'playerStats':
      return TableIcon;
    case type === 'news':
      return NewspaperIcon;
    case type === 'chats':
      return CommentOutlineIcon;
    case type === 'questionaires':
      return NewspaperIcon;
    case type === 'players':
    case type === 'selectFavoritePlayer':
      return AccountGroupIcon;
    case type === 'tierBanner':
      return WalletMembershipIcon;
    case type === 'matchBanner':
    case type === 'matchCenter':
    case type === 'matchcenter':
    case type === 'externalLiveScore':
      return StadiumIcon;
    case type === 'shop':
      return BasketIcon;
    case type === 'sponsors':
      return SitemapIcon;
    case type === 'events':
      return CalendarIcon;
    case type === 'lottery':
    case type === 'seasonBanner':
    case type === 'tickets':
      return TicketIcon;
    case type === 'offers':
      return BrightnessPercentIcon;
    case type === 'auctions':
      return GavelIcon;
    case type === 'youTubeFeed':
      return YoutubeIcon;
    case type === 'custom':
      return NewspaperIcon;
    case type === 'seasonMatchNotificationsBanner':
      return BellAlertIcon;
    case type === 'termsBanner':
      return BellAlertIcon;
    case type === 'NTFGoalFeed':
      return BellAlertIcon;
    case type === 'shorts':
      return YoutubeIcon;
    case type === 'streamsVideos':
      return YoutubeIcon;
    case type === 'clubMateLottery':
    case type === 'clubMateLotterySlim':
      return YoutubeIcon;
    case type === 'goal-sponsor':
      return ClubheartIcon;
    case type === 'company-activity-widget':
      return SitemapIcon;
    case type.toLowerCase().startsWith('forzasys'):
      return VideoIcon;
    case type.toLowerCase().startsWith('fogis'):
      return SoccerFieldIcon;
    default:
      return ImageOutlineIcon;
  }
};

const getPremium = (type) => {
  switch (true) {
    case type === 'custom':
      return true;
    default:
      return false;
  }
};

const getFeatures = (features, name) => {
  if (features?.includes(name)) {
    return true;
  }
  return false;
};

// Unlike getPremium's items (shown but dimmed as an upsell), these are
// integrations that only make sense for clubs that actually use them - so
// they're hidden entirely rather than shown-disabled when the flag is off,
// same as SidebarContent gates a whole nav category on `features?.includes`.
// Every id the backend returns for these integrations (forzasysStats,
// ForzaSysTopStats, fogisClub, ...) shares one flag per integration, hence
// the prefix match rather than an exact-id list.
// Match center, player statistics and standings only make sense for a club
// that plays matches; the `notSportsTeam` feature flag (e.g. an event or golf
// organisation) hides them from every layout column.
const SPORTS_ONLY_COMPONENTS = [
  'matchcenter',
  'stats',
  'playerstats',
  'standings',
];
const isSportsComponentHidden = (type, features) =>
  !!features?.includes('notSportsTeam') &&
  SPORTS_ONLY_COMPONENTS.includes(type.toLowerCase());

const isFeatureVisible = (type, features) => {
  const normalized = type.toLowerCase();
  if (isSportsComponentHidden(type, features)) {
    return false;
  }
  if (normalized.startsWith('forzasys')) {
    return features?.includes('forzasys');
  }
  if (normalized.startsWith('fogis')) {
    return features?.includes('fogis');
  }
  if (normalized === 'externallivescore') {
    return features?.includes('externalLiveScore');
  }
  if (normalized === 'ntfgoalfeed') {
    return features?.includes('forzasys');
  }
  return true;
};

const AppLayoutPage = () => {
  const config = useConfig();

  const features = config?.featureFlags;
  const [homescreen, setHomescreen] = useState({
    Col0: {
      name: 'Components',
      items: [],
    },
  });
  const [more, setMore] = useState({
    Col4: {
      name: 'More',
      items: [],
    },
  });
  const [tabs, setTabs] = useState({
    Col2: {
      name: 'Tabs',
      items: [],
    },
  });
  const [loadingComponents, setLoadingComponents] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  // The More link being created ({}) or edited ({ item }); null when closed
  const [linkModal, setLinkModal] = useState(null);
  // The same for a divider in the More menu
  const [dividerModal, setDividerModal] = useState(null);
  // The app's own More row whose icon is being changed ({ item }), or null
  const [iconModal, setIconModal] = useState(null);
  // Whether members see My memberships at the top of More
  const [memberRowShown, setMemberRowShown] = useState(true);
  // The layout as last loaded or saved, to tell when there are changes
  const [savedLayout, setSavedLayout] = useState(null);
  const [saved, setSaved] = useState(false);
  // Set once a load is in the lists: what they show then is the saved layout
  const [loadedLayout, setLoadedLayout] = useState(false);
  // The same object while the modal is open: the form starts over when it changes
  const editingLink = useMemo(
    () =>
      linkModal?.item
        ? { ...linkModal.item.link, icon: linkModal.item.icon }
        : null,
    [linkModal]
  );

  const p = partial('appLayout');
  const s = partial('shared');

  const orderMapper = (e, i) => {
    // No icon: a blank title isn't saved
    if (isDividerItem(e)) {
      const title = (e.divider.title || '').trim();
      return {
        id: e.id,
        value: DIVIDER_VALUE,
        order: i + 1,
        layout: e.divider.layout,
        ...(title ? { title } : {}),
      };
    }
    if (isLinkItem(e)) {
      return withIcon(
        {
          id: e.id,
          value: LINK_VALUE,
          order: i + 1,
          ...e.link,
        },
        e
      );
    }
    if (e.id.includes('spotlight')) {
      const entityId = e.id.split('spotlight-')[1];
      return {
        id: e.id,
        value: e.id,
        order: i + 1,
        entityId,
      };
    }
    return withIcon(
      {
        id: e.id,
        value: e.id,
        order: i + 1,
      },
      e
    );
  };

  // What's saved: the three areas' lists, and My memberships when hidden
  const buildPayload = () => {
    const payload = {
      home: (homescreen.Col1?.items || []).map(orderMapper),
      tabs: (tabs.Col1?.items || []).map(orderMapper),
      more: [
        ...(more.Col1?.items || []).map(orderMapper),
        ...(memberRowShown
          ? []
          : [
              {
                id: MEMBER_ROW_VALUE,
                value: MEMBER_ROW_VALUE,
                order: 0,
                hidden: true,
              },
            ]),
      ],
    };
    if (!Config.monolithEnabled) {
      payload.home = payload?.home?.filter(
        (item) => item?.id !== 'shop' && item?.id !== 'matchBanner'
      );
      payload.tabs = payload?.tabs?.filter(
        (item) => item?.id !== 'shop' && item?.id !== 'matchBanner'
      );
      payload.more = payload?.more?.filter(
        (item) => item?.id !== 'shop' && item?.id !== 'matchBanner'
      );
    }
    return payload;
  };
  const currentLayout = JSON.stringify(buildPayload());
  const dirty = savedLayout !== null && currentLayout !== savedLayout;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const payload = buildPayload();
    setLoadingComponents(true);
    try {
      await Api.app_layout.app.create(config.id, payload);
      toast.success('Layout updated');
      setSavedLayout(JSON.stringify(payload));
      setSaved(true);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
    }
    setLoadingComponents(false);
  };
  const getInitialData = async () => {
    setLoadingComponents(true);
    try {
      let getLayout = null;
      const [
        { data: layoutOptions },
        getLayoutResponse,
        { data: spotlightGroups },
      ] = await Promise.all([
        Api.app_layout.app.getComponents(),
        Api.app_layout.app.all(config.id),
        Api.spotlight.getGroups(
          { page: 1, take: 100 },
          { order: 'createdAt', direction: 'DESC' }
        ),
      ]);
      getLayout = getLayoutResponse;
      if (Object.keys(getLayout.data).length === 0) {
        getLayout = {
          data: {
            home: [],
            tabs: [],
            more: [],
          },
        };
      }
      const { home: dbHome, tabs: dbTabs, more: dbMore } = getLayout.data;
      const { home, tabs: _tabs, more: _more } = layoutOptions;
      const homeOptions = home
        .filter((item) => item !== 'spotlight')
        .map((e) => ({ id: e, name: componentName(e) }));
      spotlightGroups.forEach((group) =>
        homeOptions.push({
          id: `spotlight-${group.id}`,
          name: `${group.title}`,
        })
      );
      const homeObject = {
        Col0: {
          name: 'Components',
          items: homeOptions.filter(
            (e) =>
              !dbHome.some((h) => h.value === e.id) &&
              isFeatureVisible(e.id, features)
          ),
        },
        Col1: {
          name: 'Homescreen',
          items: dbHome
            .map((e) => {
              if (e.value.includes('spotlight')) {
                const spotId = e.value.split('spotlight-')[1];
                const foundSpotlight = spotlightGroups.find(
                  (item) => item.id === spotId
                );
                if (!foundSpotlight) {
                  return null;
                }
                return {
                  id: e.value,
                  name: `${p('spotlight')} ${foundSpotlight.title}`,
                };
              }
              return { id: e.value, name: componentName(e.value) };
            })
            .filter(
              (item) => !!item && !isSportsComponentHidden(item.id, features)
            ),
        },
      };
      // Not shown where the club has no monolith: the kiosk and match banner
      const inTabLists = (value) =>
        Config.monolithEnabled || (value !== 'shop' && value !== 'matchBanner');
      const tabsObject = {
        Col0: {
          name: 'Tab alternativ',
          items: _tabs
            .filter(
              (e) =>
                inTabLists(e) &&
                !dbTabs.some((h) => h.value === e) &&
                isFeatureVisible(e, features)
            )
            .map((e) => ({ id: e, name: componentName(e) })),
        },
        Col1: {
          name: 'Tabs in-app',
          items: dbTabs
            .filter(
              (e) =>
                inTabLists(e.value) &&
                !isSportsComponentHidden(e.value, features)
            )
            .map((e) => ({ id: e.value, name: componentName(e.value) })),
        },
      };
      // Not in either list: My memberships (pinned, with its own switch),
      // and the kiosk and match banner where the club has no monolith
      const inMoreLists = (value) =>
        value !== MEMBER_ROW_VALUE &&
        (Config.monolithEnabled ||
          (value !== 'shop' && value !== 'matchBanner'));
      const moreObject = {
        Col0: {
          name: 'More alternativ',
          items: _more
            .filter(
              (e) =>
                e !== DIVIDER_VALUE &&
                inMoreLists(e) &&
                !dbMore.some((h) => h.value === e) &&
                isFeatureVisible(e, features)
            )
            .map((e) => ({ id: e, name: componentName(e) })),
        },
        Col1: {
          name: 'More screen in-app',
          items: dbMore
            .filter(
              (e) =>
                inMoreLists(e.value) &&
                !isSportsComponentHidden(e.value, features)
            )
            .map((e) => {
              if (e.value === DIVIDER_VALUE) {
                return {
                  id: e.id,
                  name: e.title || p('divider'),
                  divider: {
                    title: e.title || '',
                    layout: e.layout === 'grid' ? 'grid' : 'list',
                  },
                };
              }
              return e.value === LINK_VALUE
                ? {
                    id: e.id,
                    name: e.label,
                    icon: e.icon,
                    link: {
                      label: e.label,
                      contentType: e.contentType,
                      contentId: e.contentId,
                    },
                  }
                : { id: e.value, name: componentName(e.value), icon: e.icon };
            }),
        },
      };
      setHomescreen(homeObject);
      setTabs(tabsObject);
      setMore(moreObject);
      setMemberRowShown(
        !dbMore.some((e) => e.value === MEMBER_ROW_VALUE && e.hidden)
      );
      setLoadedLayout(true);
      setLoadingComponents(false);
    } catch (err) {
      toast.error(err || s('somethingWentWrong'));
      setLoadingComponents(false);
    }
  };
  useEffect(() => {
    getInitialData();
  }, []);
  useEffect(() => {
    if (!loadedLayout) return;
    setSavedLayout(currentLayout);
    setSaved(false);
    setLoadedLayout(false);
  }, [loadedLayout, currentLayout]);
  // Leaving with unsaved changes (closing the tab; in-app links use Prompt)
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const onDragEnd = async (result, columns, setColumns, dropfieldName) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (
      dropfieldName === 'tabs' &&
      destination.droppableId === 'Col1' &&
      source.droppableId !== 'Col1' &&
      columns[destination.droppableId].items.length >= MAX_TABS
    ) {
      toast.error(p('bottomFull'));
      return;
    }
    let columnObject = {};
    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];
      const sourceItems = [...sourceColumn.items];
      const destItems = [...destColumn.items];
      const [removed] = sourceItems.splice(source.index, 1);
      // A link or divider isn't one of the available components: out of the
      // menu, it's gone
      if (
        !(
          (isLinkItem(removed) || isDividerItem(removed)) &&
          destination.droppableId === 'Col0'
        )
      ) {
        destItems.splice(destination.index, 0, removed);
      }
      columnObject = {
        ...columns,
        [source.droppableId]: {
          ...sourceColumn,
          items: sourceItems,
        },
        [destination.droppableId]: {
          ...destColumn,
          items: destItems,
        },
      };
      setColumns(columnObject);
    } else {
      const column = columns[source.droppableId];
      const copiedItems = [...column.items];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);
      columnObject = {
        ...columns,
        [source.droppableId]: {
          ...column,
          items: copiedItems,
        },
      };
      setColumns(columnObject);
    }
  };
  // A new link or divider goes where its card was dropped ({ index }), or
  // at the end of the menu when added with +
  const insertInMenu = (entry, index) => {
    const items = [...more.Col1.items];
    items.splice(Number.isInteger(index) ? index : items.length, 0, entry);
    return items;
  };
  // Links are added where they were dropped, and edited where they are
  const saveLink = ({ icon, ...link }) => {
    const editing = linkModal?.item;
    const items = editing
      ? more.Col1.items.map((item) =>
          item.id === editing.id
            ? { ...item, name: link.label, link, icon }
            : item
        )
      : insertInMenu(
          { id: newLinkId(), name: link.label, link, icon },
          linkModal?.index
        );
    setMore({ ...more, Col1: { ...more.Col1, items } });
    setLinkModal(null);
  };
  // An icon for one of the app's own rows ('' = back to its default)
  const saveIcon = (icon) => {
    const items = more.Col1.items.map((item) =>
      item.id === iconModal?.item?.id ? { ...item, icon } : item
    );
    setMore({ ...more, Col1: { ...more.Col1, items } });
    setIconModal(null);
  };
  // Dividers, like links, go where they were dropped
  const saveDivider = (divider) => {
    const editing = dividerModal?.item;
    const name = divider.title || p('divider');
    const items = editing
      ? more.Col1.items.map((item) =>
          item.id === editing.id ? { ...item, name, divider } : item
        )
      : insertInMenu(
          { id: newDividerId(), name, divider },
          dividerModal?.index
        );
    setMore({ ...more, Col1: { ...more.Col1, items } });
    setDividerModal(null);
  };
  // Out of the menu: a link or a divider is gone, one of the app's own
  // components is available again
  const removeMoreItem = (itemId) => {
    const removed = more.Col1.items.find((item) => item.id === itemId);
    const items = more.Col1.items.filter((item) => item.id !== itemId);
    const backToAvailable =
      removed && !isLinkItem(removed) && !isDividerItem(removed);
    setMore({
      ...more,
      Col0: backToAvailable
        ? {
            ...more.Col0,
            items: [...more.Col0.items, { id: removed.id, name: removed.name }],
          }
        : more.Col0,
      Col1: { ...more.Col1, items },
    });
  };
  // One of the app's components, with +: to the end of the menu
  const addMoreItem = (added) => {
    setMore({
      ...more,
      Col0: {
        ...more.Col0,
        items: more.Col0.items.filter((item) => item.id !== added.id),
      },
      Col1: { ...more.Col1, items: [...more.Col1.items, added] },
    });
  };
  const toggleDividerLayout = (toggled) => {
    const items = more.Col1.items.map((item) =>
      item.id === toggled.id
        ? {
            ...item,
            divider: {
              ...item.divider,
              layout: item.divider.layout === 'grid' ? 'list' : 'grid',
            },
          }
        : item
    );
    setMore({ ...more, Col1: { ...more.Col1, items } });
  };
  // A Link or Divider card dropped in the menu opens its form; what's saved
  // goes where the card was dropped. Everything else moves as in the other tabs
  const onMoreDragEnd = (result) => {
    const { source, destination } = result;
    if (source.droppableId === PALETTE) {
      if (destination?.droppableId === 'Col1') {
        const open =
          source.index === PALETTE_LINK ? setLinkModal : setDividerModal;
        open({ index: destination.index });
      }
      return;
    }
    onDragEnd(result, more, setMore, 'more');
  };
  // A More row's icon: the app's, when chosen (or a link's), else the
  // component's own
  const renderMoreIcon = (item) => {
    if (item.id === 'clubMateLottery' || item.id === 'clubMateLotterySlim') {
      return <img src="/assets/icons/vendors/clubmate.svg" alt="" />;
    }
    if (item.id === 'streamsVideo') {
      return <img src="/assets/icons/vendors/Streams.png" alt="" />;
    }
    const appIcon = appIconOf(item);
    if (appIcon) {
      return <AppIcon name={appIcon} size={18} />;
    }
    const IconComponent = getIconComponent(item.id);
    return <IconComponent size={18} />;
  };
  // The bottom menu's + and -: to the end of the bar (while there's room),
  // and back to the available components
  const addTab = (added) => {
    if (tabs.Col1.items.length >= MAX_TABS) return;
    setTabs({
      ...tabs,
      Col0: {
        ...tabs.Col0,
        items: tabs.Col0.items.filter((item) => item.id !== added.id),
      },
      Col1: { ...tabs.Col1, items: [...tabs.Col1.items, added] },
    });
  };
  const removeTab = (removed) => {
    setTabs({
      ...tabs,
      Col0: { ...tabs.Col0, items: [...tabs.Col0.items, removed] },
      Col1: {
        ...tabs.Col1,
        items: tabs.Col1.items.filter((item) => item.id !== removed.id),
      },
    });
  };
  const isMoreItemDisabled = (item) =>
    getPremium(item?.id) ? !getFeatures(features, item?.id) : false;
  const renderDragNDrop = (columns, setColumns, dropfieldName) => (
    <DragDropContext
      onDragEnd={(result) =>
        onDragEnd(result, columns, setColumns, dropfieldName)
      }
    >
      {Object.entries(columns).map(([columnId, column]) => {
        return (
          <Col key={column.name} xs={6} sm={6} md={6} xl={6}>
            <strong>
              {column.name.split(' ').pop() === 'in-app' ||
              column.name === 'Homescreen'
                ? s('Active')
                : s('Available')}
            </strong>
            <Droppable droppableId={columnId} key={columnId}>
              {(provided, snapshot) => {
                return (
                  <div
                    className="comCol"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    style={{
                      background: snapshot.isDraggingOver
                        ? '#d9edff'
                        : '#F9FAFB',
                      borderWidth: 1,
                      borderRadius: 3,
                      borderStyle: 'solid',
                      borderColor: snapshot.isDraggingOver
                        ? '#0469DC'
                        : '#D7DAE0',
                    }}
                  >
                    {column.items.length === 0 && (
                      <div className="my-5">
                        <EmptyStateInfo
                          icon="layout"
                          title={s('emptyStateTitle')}
                          text={
                            column.name.split(' ').pop() === 'in-app'
                              ? p('emptyAppComponents')
                              : ''
                          }
                        />
                      </div>
                    )}
                    {column.items.map((item, index) => {
                      const last =
                        column.items.length === index + 1
                          ? 0
                          : '1px dashed #D7DAE0';
                      const IconComponent = getIconComponent(item.id);
                      if (
                        !Config.monolithEnabled &&
                        (item?.id === 'shop' || item?.id === 'matchBanner')
                      ) {
                        return <div />;
                      }
                      const disabled = getPremium(item?.id)
                        ? !getFeatures(features, item?.id)
                        : false;
                      return (
                        <Draggable
                          key={item.id}
                          draggableId={`${item.id}${columnId}`}
                          index={index}
                          isDragDisabled={disabled}
                        >
                          {(provided2, snapshot2) => {
                            return (
                              <div
                                className=""
                                ref={provided2.innerRef}
                                {...provided2.draggableProps}
                                {...provided2.dragHandleProps}
                                style={{
                                  opacity: disabled ? 0.4 : 1,
                                  backgroundColor: snapshot2.isDragging
                                    ? '#F5FAFF'
                                    : 'white',
                                  color: snapshot2.isDragging
                                    ? '#0469DC !important'
                                    : 'black',
                                  padding: 10,
                                  borderTop: snapshot2.isDragging
                                    ? '1px solid #0469DC'
                                    : 0,
                                  borderLeft: snapshot2.isDragging
                                    ? '1px solid #0469DC'
                                    : 0,
                                  borderRight: snapshot2.isDragging
                                    ? '1px solid #0469DC'
                                    : 0,
                                  borderBottom: snapshot2.isDragging
                                    ? '1px solid #0469DC'
                                    : last,
                                  ...provided2.draggableProps.style,
                                }}
                              >
                                <div
                                  style={{
                                    margin: '0 auto',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}
                                >
                                  <DragIcon
                                    style={{ widht: 30, color: '#D7DAE0' }}
                                    className="mt-1"
                                  />

                                  {(item.id === 'clubMateLottery' ||
                                    item.id === 'clubMateLotterySlim') && (
                                    <img
                                      src="/assets/icons/vendors/clubmate.svg"
                                      alt=""
                                      style={{
                                        margin: 5,
                                        padding: 3,
                                        borderRadius: 6,
                                        fill: 'rgb(18, 27, 43)',
                                        borderWidth: 1,
                                        borderColor: '#601fa2',
                                        borderStyle: 'solid',
                                        height: 30,
                                        width: 30,
                                      }}
                                    />
                                  )}
                                  {item.id === 'streamsVideo' && (
                                    <img
                                      src="/assets/icons/vendors/Streams.png"
                                      alt=""
                                      style={{
                                        margin: 5,
                                        padding: 3,
                                        borderRadius: 6,
                                        fill: 'rgb(11, 72, 177)',
                                        borderWidth: 1,
                                        borderColor: '#0b48b1',
                                        borderStyle: 'solid',
                                        height: 30,
                                        width: 30,
                                      }}
                                    />
                                  )}
                                  {item.id !== 'clubMateLottery' &&
                                    item.id !== 'clubMateLotterySlim' &&
                                    item.id !== 'streamsVideo' && (
                                      <IconComponent
                                        style={{
                                          margin: '5px 5px 0',
                                          padding: 3,
                                          widht: 10,
                                          borderRadius: 6,
                                          fill: snapshot2.isDragging
                                            ? '#0469DC'
                                            : '#121B2B',
                                          backgroundColor: '#D7E9FE',
                                        }}
                                        size={30}
                                      />
                                    )}
                                  {
                                    /* getPremium(item.id) */ false && (
                                      <StarCircleIcon
                                        style={{
                                          position: 'relative',
                                          right: 11,
                                          top: -11,
                                          color: '#703FF3',
                                          width: 12,
                                        }}
                                      />
                                    )
                                  }
                                  {/* Shrinks, so a long name never pushes the
                                      row's buttons out */}
                                  <div style={{ minWidth: 0 }}>
                                    <p
                                      style={{
                                        display: 'block',
                                        marginTop: 5,
                                        overflowWrap: 'anywhere',
                                        fontSize: 12,
                                        fontWeight: 700,
                                        lineHeight: '12px',
                                        color: snapshot2.isDragging
                                          ? '#0469DC'
                                          : 'black',
                                      }}
                                    >
                                      {item.name}
                                    </p>
                                    {item.id === 'streamsVideo' && (
                                      <p
                                        style={{
                                          display: 'block',
                                          marginTop: 0,
                                          fontSize: 8,
                                          fontWeight: 600,
                                          lineHeight: '12px',
                                          color: snapshot2.isDragging
                                            ? '#0469DC'
                                            : 'gray',
                                        }}
                                      >
                                        By Streams
                                      </p>
                                    )}
                                    {(item.id === 'clubMateLottery' ||
                                      item.id === 'clubMateLotterySlim') && (
                                      <p
                                        style={{
                                          display: 'block',
                                          marginTop: 0,
                                          fontSize: 8,
                                          fontWeight: 600,
                                          lineHeight: '12px',
                                          color: snapshot2.isDragging
                                            ? '#0469DC'
                                            : 'gray',
                                        }}
                                      >
                                        By Clubmate
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          }}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </Col>
        );
      })}
    </DragDropContext>
  );
  return (
    <>
      <Prompt when={dirty} message={p('appLeaveWarning')} />
      <form className="form" onSubmit={handleSubmit}>
        <Loading loading={loadingComponents} />
        <Card>
          <CardBody style={{ padding: 0 }}>
            <NavTab
              tabs={[
                { tab: p('home') },
                { tab: p('bottomMenu') },
                { tab: p('more') },
              ]}
              activeTab={activeTab}
              setTab={setActiveTab}
              size="sm"
            />
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <div className="px-4">
                  <Row>
                    <Col md={12} lg={12}>
                      <div style={{ maxWidth: '450px', margin: '20px' }}>
                        <h2>{p('home')}</h2>
                        <p>{p('homeDesc')}</p>
                      </div>
                    </Col>
                    <Col md={8}>
                      <Row>
                        {renderDragNDrop(
                          homescreen,
                          setHomescreen,
                          'homescreen'
                        )}
                      </Row>
                    </Col>
                    <div className="app-t" style={{ marginTop: 21 }}>
                      <AppMock
                        home={homescreen?.Col1?.items}
                        tabs={tabs.Col1?.items}
                      />
                    </div>
                  </Row>
                </div>
              </TabPane>
              <TabPane tabId="2">
                <div className="px-4">
                  <Row>
                    <Col md={12}>
                      <BottomMenuEditor
                        tabs={tabs}
                        onDragEnd={(result) =>
                          onDragEnd(result, tabs, setTabs, 'tabs')
                        }
                        renderIcon={renderMoreIcon}
                        isDisabled={isMoreItemDisabled}
                        onAdd={addTab}
                        onRemove={removeTab}
                        preview={
                          <HomePhonePreview
                            home={homescreen?.Col1?.items}
                            tabs={tabs.Col1?.items}
                            chosenIcon={appIconOf}
                          />
                        }
                      />
                    </Col>
                  </Row>
                </div>
              </TabPane>
              <TabPane tabId="3">
                <div className="px-4">
                  <Row>
                    <Col md={12}>
                      <MoreMenuEditor
                        more={more}
                        onDragEnd={onMoreDragEnd}
                        renderIcon={renderMoreIcon}
                        isDisabled={isMoreItemDisabled}
                        onAdd={addMoreItem}
                        onRemove={(item) => removeMoreItem(item.id)}
                        onAddLink={() => setLinkModal({})}
                        onAddDivider={() => setDividerModal({})}
                        onEditLink={(item) => setLinkModal({ item })}
                        onEditDivider={(item) => setDividerModal({ item })}
                        onChangeIcon={(item) => setIconModal({ item })}
                        onToggleLayout={toggleDividerLayout}
                        memberRowShown={memberRowShown}
                        onToggleMemberRow={() =>
                          setMemberRowShown((shown) => !shown)
                        }
                        preview={
                          <MorePhonePreview
                            menu={more.Col1?.items}
                            tabs={tabs.Col1?.items}
                            memberRow={memberRowShown}
                            clubLogo={config?.club_logo}
                            chosenIcon={appIconOf}
                          />
                        }
                      />
                    </Col>
                  </Row>
                </div>
              </TabPane>
            </TabContent>
            <div className="wl-savebar">
              <span className="wl-savebar__status">
                {dirty && (
                  <span className="wl-unsaved">{p('unsavedChanges')}</span>
                )}
                {!dirty && saved && (
                  <span className="wl-saved">
                    <CheckIcon size={16} />
                    {p('allSaved')}
                  </span>
                )}
              </span>
              <span className="wl-savebar__actions">
                {dirty && (
                  <Button
                    type="button"
                    outline
                    color="secondary"
                    size="sm"
                    className="mb-0"
                    onClick={getInitialData}
                  >
                    {p('discard')}
                  </Button>
                )}
                <Button
                  type="submit"
                  color="primary"
                  size="sm"
                  className="mb-0"
                  disabled={!dirty || loadingComponents}
                >
                  {p('saveAll')}
                </Button>
              </span>
            </div>
          </CardBody>
        </Card>
      </form>
      {/* Outside the page's form: its own form's submit would reach this one
        through the portal */}
      <MoreLinkModal
        isOpen={!!linkModal}
        link={editingLink}
        onClose={() => setLinkModal(null)}
        onSave={saveLink}
      />
      <DividerModal
        isOpen={!!dividerModal}
        divider={dividerModal?.item?.divider || null}
        onClose={() => setDividerModal(null)}
        onSave={saveDivider}
        onDelete={() => {
          removeMoreItem(dividerModal?.item?.id);
          setDividerModal(null);
        }}
      />
      <MoreIconModal
        isOpen={!!iconModal}
        item={iconModal?.item || null}
        onClose={() => setIconModal(null)}
        onSave={saveIcon}
      />
    </>
  );
};

export default AppLayoutPage;
const AppMock = ({ home = [], tabs = [], more = [] }) => {
  const components = (component) => {
    switch (component) {
      case 'tickets':
        return {
          name: componentName('tickets'),
          icon: 'Ticket',
        };
      case 'matchcenter':
        return {
          name: componentName('matchcenter'),
          icon: 'calendar-outline',
        };
      case 'players':
        return {
          name: componentName('players'),
          icon: 'hockey-helmet',
        };
      case 'shop':
        return {
          name: componentName('shop'),
          icon: 'shopping-cart',
        };
      case 'events':
        return {
          name: componentName('events'),
          icon: 'calendar',
        };
      case 'lottery':
        return {
          name: componentName('lottery'),
          icon: 'gambling',
        };
      case 'auctions':
        return {
          name: componentName('auctions'),
          icon: 'hammer',
        };
      case 'offers':
        return {
          name: componentName('offers'),
          icon: 'Offers',
        };
      case 'stats':
        return {
          name: componentName('stats'),
          icon: 'stats',
        };
      case 'sponsors':
        return {
          name: componentName('sponsors'),
          icon: 'user',
        };
      case 'tierBanner':
        return {
          name: componentName('tierBanner'),
          icon: 'user',
        };
      case 'questionaires':
        return {
          name: componentName('questionaires'),
          icon: 'calendar',
        };
      case 'custom':
        return {
          name: componentName('custom'),
          icon: 'link',
        };
      case 'youTubeFeed':
        return {
          name: componentName('youTubeFeed'),
          icon: 'youtube',
        };
      case 'news':
        return {
          name: componentName('news'),
          icon: 'news',
        };
      case 'chats':
        return {
          name: componentName('chats'),
          icon: () => (
            <div style={{ paddingRight: 12 }}>
              <CommentOutlineIcon size={20} />
            </div>
          ),
        };
      case 'shorts':
        return {
          name: componentName('shorts'),
          icon: 'shorts',
        };
      case 'company-activity-widget':
        return {
          name: componentName('company-activity-widget'),
          icon: 'sitemap',
        };
      case 'externalLiveScore':
        return {
          name: componentName('externalLiveScore'),
          icon: 'calendar-outline',
        };
      case 'selectFavoritePlayer':
        return {
          name: componentName('selectFavoritePlayer'),
          icon: 'hockey-helmet',
        };

      default:
        return {
          name: component,
          icon: 'box',
        };
    }
  };
  const groups = [
    ['tierBanner'],
    ['news', 'youTubeFeed', 'custom'],
    ['auctions', 'lottery', 'questionaires', 'chats'],
    ['sponsors', 'offers', 'events'],
    ['players', 'stats', 'matchcenter', 'externalLiveScore'],
  ];
  const groupdMore = values(
    groupBy((e) => groups.findIndex((group) => group.includes(e.id)), more)
  );
  // With dividers the menu is grouped by them instead: each starts a group
  // of the items up to the next one (items before the first: untitled)
  const groupByDividers = (items) => {
    const result = [];
    items.forEach((item) => {
      if (isDividerItem(item)) {
        result.push({ key: item.id, ...item.divider, items: [] });
        return;
      }
      if (!result.length) {
        result.push({ key: item.id, title: '', layout: 'list', items: [] });
      }
      result[result.length - 1].items.push(item);
    });
    return result;
  };
  const moreGroups = more.some(isDividerItem)
    ? groupByDividers(more)
    : groupdMore.map((group) => ({
        key: group[0].id,
        title: '',
        layout: 'list',
        items: group,
      }));
  // A link shows its own name; it's in no group, so after the others. A row
  // with an app icon (chosen, or a link's) shows that one.
  const mockItem = (item) => {
    const base = isLinkItem(item) ? { name: item.name } : components(item.id);
    const appIcon = appIconOf(item);
    return appIcon
      ? { ...base, icon: ({ size }) => <AppIcon name={appIcon} size={size} /> }
      : base;
  };
  const renderMockIcon = (id, className = '', item = null) => {
    const icon = (item ? mockItem(item) : components(id))?.icon || 'calendar';

    if (typeof icon === 'string') {
      return <span className={`qricon-${icon} ${className}`} />;
    }

    const IconComponent = icon;
    return <IconComponent className={className} size={20} />;
  };

  return (
    <div
      className="temp"
      style={{ border: '3px solid black', borderRadius: 6 }}
    >
      <div className="templateApp">
        <div className="header">
          <img src="/assets/files/homepage/club-topbar-logo.png" alt="" />
        </div>
        {home.map((v) => {
          if (
            v.id.includes('spotlight') ||
            v.id.includes('youTubeFeed') ||
            v.id.includes('externalLiveScore') ||
            v.id.includes('NTFGoalFeed') ||
            v.id.includes('custom')
          ) {
            return (
              <div key={v.id}>
                <p
                  style={{
                    backgroundColor: 'white',
                    fontSize: 13,
                    fontWeight: 'bold',
                    paddingLeft: 10,
                    color: 'black',
                    paddingTop: 7,
                  }}
                >
                  {v.name}
                </p>
                <img
                  alt={v.id}
                  src="/assets/files/homepage/new-spotlight-homescreen.png"
                />
              </div>
            );
          }
          return (
            <img
              alt={v.id}
              key={v.id}
              src={`/assets/files/homepage/${v.id}-homescreen.png`}
            />
          );
        })}
        {moreGroups.map((group) => (
          <React.Fragment key={group.key}>
            {group.title && <p className="tempGroupTitle">{group.title}</p>}
            {group.items.length > 0 && (
              <div
                className={`tempGroup${
                  group.layout === 'grid' ? ' tempGroup--grid' : ''
                }`}
              >
                {group.items.map((item) =>
                  // Grid: two tiles per row, the icon over the name
                  group.layout === 'grid' ? (
                    <div className="tempGroupTile" key={item.id}>
                      {renderMockIcon(item.id, '', item)}
                      <span>{mockItem(item).name}</span>
                    </div>
                  ) : (
                    <div className="tempGroupItem" key={item.id}>
                      <div className="tempGroupItemIconText">
                        {renderMockIcon(item.id, '', item)}
                        <span>{mockItem(item).name}</span>
                      </div>
                      <div className="tempGroupItemRight">
                        <span className="sidebar__link-icon icon-chevron-right" />
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="templateTab first">
        <div className="templateTabElement">
          <span className="qricon-Homea fontSize-20" />
        </div>
        {tabs.map((v) => (
          <div className="templateTabElement" key={v.id}>
            {renderMockIcon(v.id, 'fontSize-20')}
          </div>
        ))}
        <div className="templateTabElement">
          <span className="qricon-menu-circle1 fontSize-20" />
        </div>
      </div>
    </div>
  );
};
